import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Calendar, MapPin, ChevronLeft, Search, Users as UsersIcon,
  RefreshCw, Save, Check, AlertCircle, UserPlus, Trophy,
} from "lucide-react";
import GlassPanel from "@/components/GlassPanel";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/context/AuthContext";

// 10 canonical tests, same keys + units as the athlete-side form.
const TESTS = [
  { key: "deadlift_kg",         label: "DEADLIFT (1RM)",       unit: "kg" },
  { key: "bench_kg",            label: "BENCH PRESS (1RM)",    unit: "kg" },
  { key: "forty_yard_sec",      label: "40-YARD DASH",         unit: "s"  },
  { key: "shuttle_5_10_5_sec",  label: "5-10-5 SHUTTLE",       unit: "s"  },
  { key: "vertical_jump_cm",    label: "VERTICAL JUMP",        unit: "cm" },
  { key: "broad_jump_cm",       label: "BROAD JUMP",           unit: "cm" },
  { key: "five_min_shuttle_m",  label: "5-MIN SHUTTLE",        unit: "m"  },
  { key: "mile_sec",            label: "MILE RUN",             unit: "s"  },
  { key: "dead_hang_sec",       label: "DEAD HANG",            unit: "s"  },
  { key: "single_leg_stand_sec",label: "SINGLE-LEG STAND",     unit: "s"  },
];

/**
 * Volunteer scoring UI for a single event. Rather than the admin's
 * one-athlete-all-tests modal, this deck picks ONE test at a time and
 * lets the volunteer enter that test's value for every athlete on the
 * roster. Each save hits the non-destructive single-test endpoint so
 * the athlete's other test values are preserved.
 *
 * The commit button is intentionally hidden — only admins can push
 * staged scores to the athlete's official history.
 */
export default function VolunteerEventDeck({ event, onBack, compact = false }) {
  const [testKey, setTestKey] = useState(TESTS[0].key);
  const [data, setData] = useState(null); // { event, roster, counts }
  const [query, setQuery] = useState("");
  const [globalHits, setGlobalHits] = useState([]);
  const [globalBusy, setGlobalBusy] = useState(false);
  const [adding, setAdding] = useState(null);
  // Per-athlete input drafts keyed by userId — value stays local until save.
  const [drafts, setDrafts] = useState({});
  const [savingRow, setSavingRow] = useState(null);
  // Row-level status: "saved" | "error" | undefined. Clears after ~2s.
  const [rowStatus, setRowStatus] = useState({});
  const statusTimers = useRef({});

  const test = TESTS.find((t) => t.key === testKey) || TESTS[0];

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/events/${event.id}/scoring`);
      setData(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }, [event.id]);

  useEffect(() => { load(); }, [load]);

  // Reset drafts when switching tests so the previous test's inputs don't leak.
  useEffect(() => { setDrafts({}); setRowStatus({}); }, [testKey]);

  // Debounced global athlete search — used to add non-roster athletes.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setGlobalHits([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setGlobalBusy(true);
      try {
        const { data } = await api.get("/admin/search", { params: { q } });
        if (cancelled) return;
        const rosterIds = new Set((currentDataRef.current?.roster || []).map((r) => r.user.id));
        const filtered = (data.users || [])
          .filter((u) => u.role !== "admin" && !rosterIds.has(u.id));
        setGlobalHits(filtered);
      } catch {
        if (!cancelled) setGlobalHits([]);
      } finally {
        if (!cancelled) setGlobalBusy(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);
  // Ref so the debounced search can see the freshest roster.
  const currentDataRef = useRef(null);
  useEffect(() => { currentDataRef.current = data; }, [data]);

  const filteredRoster = useMemo(() => {
    const rows = data?.roster || [];
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const u = r.user || {};
      return (
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.public_id || "").toLowerCase().includes(q)
      );
    });
  }, [data, query]);

  const setDraft = (uid, val) => setDrafts((d) => ({ ...d, [uid]: val }));

  const markStatus = (uid, status) => {
    setRowStatus((s) => ({ ...s, [uid]: status }));
    if (statusTimers.current[uid]) clearTimeout(statusTimers.current[uid]);
    statusTimers.current[uid] = setTimeout(() => {
      setRowStatus((s) => {
        const next = { ...s };
        delete next[uid];
        return next;
      });
    }, 2000);
  };

  const saveRow = async (row) => {
    const uid = row.user.id;
    const raw = drafts[uid];
    // Interpret empty string as "clear this test" so volunteers can undo.
    const value = raw === "" || raw === undefined || raw === null ? null : parseFloat(raw);
    if (raw !== null && raw !== undefined && raw !== "" && Number.isNaN(value)) {
      toast.error(`${row.user.name}: enter a number for ${test.label}`);
      markStatus(uid, "error");
      return;
    }
    setSavingRow(uid);
    try {
      const { data: res } = await api.post(`/admin/events/${event.id}/scores/single`, {
        user_id: uid, test_key: testKey, value,
      });
      markStatus(uid, "saved");
      // Refresh just this row's staged block in-place.
      setData((d) => {
        if (!d) return d;
        const nextRoster = d.roster.map((r) => {
          if (r.user.id !== uid) return r;
          const staged = r.staged || {
            id: null, raw: {}, result: {}, height_cm: null, weight_kg: null,
            updated_at: null, committed: false,
          };
          const nextRaw = { ...(staged.raw || {}) };
          if (value === null) delete nextRaw[testKey];
          else nextRaw[testKey] = value;
          return {
            ...r,
            staged: {
              ...staged,
              raw: nextRaw,
              result: { ...(staged.result || {}), gps: res.gps },
              updated_at: new Date().toISOString(),
            },
          };
        });
        // Recount roster completion for the header block.
        const staged_count = nextRoster.filter((r) => r.staged && Object.keys(r.staged.raw || {}).length > 0).length;
        const committed = nextRoster.filter((r) => r.staged?.committed).length;
        return {
          ...d,
          roster: nextRoster,
          counts: {
            interested: nextRoster.length,
            staged: staged_count,
            committed,
            pending_commit: staged_count - committed,
          },
        };
      });
      // Clear the draft so the input reflects the saved value.
      setDrafts((d) => {
        const n = { ...d };
        delete n[uid];
        return n;
      });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      markStatus(uid, "error");
    } finally {
      setSavingRow(null);
    }
  };

  const addAthlete = async (u) => {
    setAdding(u.id);
    try {
      await api.post(`/admin/events/${event.id}/interest/${u.id}`);
      toast.success(`Added ${u.name}`);
      setQuery("");
      setGlobalHits([]);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setAdding(null);
    }
  };

  const counts = data?.counts || { interested: 0, staged: 0, committed: 0, pending_commit: 0 };
  const dateStr = event.date ? new Date(event.date).toLocaleDateString(undefined, {
    year: "numeric", month: "short", day: "2-digit",
  }) : "TBD";

  return (
    <div data-testid="volunteer-event-deck">
      {!compact && (
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div>
            <button
              onClick={onBack}
              className="gps-btn ghost mb-3"
              data-testid="volunteer-deck-back"
              aria-label="Back to event list"
            >
              <ChevronLeft size={14} /> ALL EVENTS
            </button>
            <div className="gps-label">SCORING DECK</div>
            <h2 className="gps-heading text-2xl sm:text-3xl text-white mt-1">{event.name.toUpperCase()}</h2>
            <div className="text-mute gps-mono text-[10px] tracking-[0.2em] mt-2 flex flex-wrap gap-3">
              <span className="inline-flex items-center gap-1"><Calendar size={11} /> {dateStr}</span>
              {event.location && <span className="inline-flex items-center gap-1"><MapPin size={11} /> {event.location}</span>}
              <span className="inline-flex items-center gap-1"><UsersIcon size={11} /> {counts.interested} ROSTERED</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={load}
              className="gps-btn ghost"
              data-testid="volunteer-deck-refresh"
              title="Reload roster and staged scores"
            >
              <RefreshCw size={12} /> REFRESH
            </button>
          </div>
        </div>
      )}
      {compact && (
        <div className="flex items-center justify-end mb-3">
          <button
            onClick={load}
            className="gps-btn ghost"
            data-testid="volunteer-deck-refresh"
            title="Reload roster and staged scores"
          >
            <RefreshCw size={12} /> REFRESH
          </button>
        </div>
      )}

      <GlassPanel className="p-4 mb-4" scanline>
        <div className="grid grid-cols-1 md:grid-cols-[minmax(240px,320px)_1fr] gap-3 items-end">
          <div>
            <label className="gps-label">TEST</label>
            <select
              className="gps-input w-full"
              value={testKey}
              onChange={(e) => setTestKey(e.target.value)}
              data-testid="volunteer-deck-test-select"
            >
              {TESTS.map((t) => (
                <option key={t.key} value={t.key}>{t.label} ({t.unit})</option>
              ))}
            </select>
          </div>
          <div className="relative">
            <label className="gps-label">SEARCH ROSTER OR GPS DATABASE</label>
            <div className="relative">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-mute pointer-events-none" />
              <input
                className="gps-input w-full pl-8"
                placeholder="TYPE NAME, EMAIL OR ID…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                data-testid="volunteer-deck-search"
              />
            </div>
          </div>
        </div>
      </GlassPanel>

      <GlassPanel className="overflow-hidden" data-testid="volunteer-deck-table">
        <table className="w-full text-sm border-collapse table-fixed">
          <thead>
            <tr className="border-b border-white/15">
              <th className="p-3 text-center gps-mono text-[10px] tracking-[0.2em] text-mute w-1/3">ATHLETE</th>
              <th className="p-3 text-center gps-mono text-[10px] tracking-[0.2em] text-mute w-1/3">{test.label} ({test.unit})</th>
              <th className="p-3 text-center gps-mono text-[10px] tracking-[0.2em] text-mute w-1/3">ACTION</th>
            </tr>
          </thead>
          <tbody>
            {filteredRoster.map((r) => {
              const uid = r.user.id;
              const stagedVal = r.staged?.raw?.[testKey];
              const draftVal = drafts[uid];
              const status = rowStatus[uid];
              const isCommitted = !!r.staged?.committed;
              // SAVE is only actionable when there's a real change waiting to
              // be pushed. A draft counts as "dirty" whenever it differs from
              // the currently staged value (empty string == cleared).
              const isDirty = draftVal !== undefined
                && String(draftVal) !== String(stagedVal ?? "");
              return (
                <tr
                  key={uid}
                  className={`border-b border-white/5 ${isCommitted ? "opacity-60" : ""}`}
                  data-testid={`volunteer-deck-row-${uid}`}
                >
                  <td className="p-3 text-center">
                    <div className="text-white text-sm">{r.user.name}</div>
                    <div className="gps-mono text-[10px] text-cyan-400 tracking-[0.15em] mt-1">
                      {r.user.public_id || "—"}
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    {isCommitted ? (
                      <span className="text-emerald-400 gps-mono text-[10px] tracking-[0.15em]">COMMITTED</span>
                    ) : (
                      <input
                        className="gps-input w-[90px] text-center mx-auto"
                        type="number"
                        step="any"
                        maxLength={4}
                        value={draftVal !== undefined ? draftVal : (stagedVal ?? "")}
                        onChange={(e) => setDraft(uid, e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter" && isDirty) saveRow(r); }}
                        placeholder="—"
                        data-testid={`volunteer-deck-input-${uid}`}
                      />
                    )}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-2 justify-center">
                      {status === "saved" && (
                        <span className="text-emerald-400 inline-flex items-center gap-1 gps-mono text-[10px] tracking-[0.15em]" data-testid={`volunteer-deck-saved-${uid}`}>
                          <Check size={12} /> SAVED
                        </span>
                      )}
                      {status === "error" && (
                        <span className="text-rose-400 inline-flex items-center gap-1 gps-mono text-[10px] tracking-[0.15em]">
                          <AlertCircle size={12} /> ERROR
                        </span>
                      )}
                      <button
                        onClick={() => saveRow(r)}
                        disabled={savingRow === uid || isCommitted || !isDirty}
                        className="gps-btn"
                        data-testid={`volunteer-deck-save-${uid}`}
                        title={isDirty
                          ? `Save ${test.label} for ${r.user.name}`
                          : "Type a value to enable save"}
                      >
                        <Save size={12} /> {savingRow === uid ? "SAVING" : "SAVE"}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredRoster.length === 0 && globalHits.length === 0 && (
              <tr>
                <td colSpan={3} className="p-8 text-center text-mute gps-mono text-xs tracking-[0.2em]" data-testid="volunteer-deck-empty">
                  {(data?.roster || []).length === 0 && !query
                    ? "NO ATHLETES YET · SEARCH TO ADD ONE"
                    : (globalBusy ? "SEARCHING…" : "NO MATCHES")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </GlassPanel>

      {globalHits.length > 0 && (
        <GlassPanel className="mt-4 p-3" data-testid="volunteer-deck-global-panel">
          <div className="gps-mono text-[10px] text-mute tracking-[0.2em] mb-2">ADD FROM GPS DATABASE · {globalHits.length}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {globalHits.map((u) => (
              <div
                key={u.id}
                className="flex items-center justify-between p-3 border border-white/10 rounded-sm hover:bg-white/[0.04]"
                data-testid={`volunteer-deck-global-${u.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-white text-sm truncate">{u.name}</div>
                  <div className="gps-mono text-[10px] tracking-[0.15em] mt-1 flex gap-3">
                    <span className="text-cyan-400">{u.public_id || "—"}</span>
                    <span className="text-mute truncate">{u.email}</span>
                  </div>
                </div>
                <button
                  onClick={() => addAthlete(u)}
                  disabled={adding === u.id}
                  className="gps-btn ghost"
                  data-testid={`volunteer-deck-add-${u.id}`}
                  title={`Add ${u.name} to this event`}
                >
                  <UserPlus size={12} /> ADD
                </button>
              </div>
            ))}
          </div>
        </GlassPanel>
      )}

      <div className="mt-4 gps-mono text-[10px] text-mute tracking-[0.2em] flex items-center gap-2">
        <Trophy size={11} /> STAGED SCORES SHOW ON THE PUBLIC LEADERBOARD IMMEDIATELY · ONLY AN ADMIN CAN COMMIT TO OFFICIAL HISTORY
      </div>
    </div>
  );
}
